import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { SharedService } from '../../../services/shared.service';
import { StatisticsService } from '../../../services/statistics.service';

export interface DrugStatisticsCategory {
  id: string;
  label: string;
  selected: boolean;
  /** Kroll DrgGrpMast GroupID when included in the quarterly table; null = not in this report. */
  groupId: number | null;
}

export interface DrugStatisticsHomeOption {
  id: number;
  name: string;
}

export interface DrugStatisticsHomeGroup {
  pharmacyId: number;
  pharmacyName: string;
  homes: DrugStatisticsHomeOption[];
}

export interface MedicationClassReportCell {
  quarterSort: number;
  quarterLabel: string;
  totalPatients: number;
  totalPct: number;
  regPatients: number;
  regPct: number;
  prnPatients: number;
  prnPct: number;
}

export interface MedicationClassReportRow {
  groupId: number;
  medicationClass: string;
  sortOrder: number;
  cells: MedicationClassReportCell[];
}

export interface MedicationClassReportQuarter {
  label: string;
  sort: number;
}

export interface AverageRxReportCell {
  quarterSort: number;
  quarterLabel: string;
  regRate: number;
  prnRate: number;
  txRate: number;
  totalRate: number;
  regRxCount: number;
  prnRxCount: number;
  txRxCount: number;
}

export interface AverageRxReportRow {
  wardId: number | null;
  wardName: string;
  isTotal: boolean;
  sortOrder: number;
  licensedBeds: number;
  cells: AverageRxReportCell[];
}

export interface WardBedsEditor {
  wardId: number;
  wardName: string;
  licensedBeds: number;
  defaultLicensedBeds: number;
}

export interface AcpCategoryAverageRow {
  key: string;
  category: string;
  value: string;
  groupId: number | null;
  pct: number;
  /** Category = distinct REG+PRN patients; DUR = distinct OrigRx. Recalc uses licensed beds as denominator. */
  numerator: number | null;
  denominator: number | null;
}

export interface TrendChartView {
  title: string;
  groupId: number;
  categoryLabel: string;
  bars: { x: number; y: number; w: number; h: number; color: string; tooltip: string }[];
  labels: { x: number; text: string }[];
  yLabels: { y: number; text: string }[];
  trendPoints: string;
}

export interface IncidentTrendEditorRow {
  label: string;
  values: number[];
}

export interface IncidentTrendChartView {
  title: string;
  bars: { x: number; y: number; w: number; h: number; color: string; tooltip: string }[];
  labels: { x: number; text: string; lines: string[] }[];
  yLabels: { y: number; text: string }[];
  legendItems: { color: string; label: string }[];
  emptyMessage: string;
  viewWidth: number;
  viewHeight: number;
  plotLeft: number;
  plotBottom: number;
  rotateXLabels: boolean;
  editorKey: string;
  tableOpen: boolean;
  tableColumnLabels: string[];
  tableRows: IncidentTrendEditorRow[];
}

@Component({
  selector: 'app-misc-drug-statistics',
  templateUrl: './drug-statistics.component.html',
  styleUrls: ['./drug-statistics.component.css']
})
export class MiscDrugStatisticsComponent implements OnInit {

  readonly selectHomePlaceholder = 'Select a home...';
  readonly selectUnitPlaceholder = 'Select a home first...';

  /** 0 = All pharmacies (3 and 26). */
  selectedOrganizationPharmacyId = 0;
  organizationOptions: { id: number; name: string }[] = [
    { id: 0, name: 'All' },
    { id: 3, name: 'Advantage Care Pharmacy' },
    { id: 26, name: 'Adelaide Pharmacy' }
  ];

  homeGroups: DrugStatisticsHomeGroup[] = [];
  visibleHomeGroups: DrugStatisticsHomeGroup[] = [];
  homesLoading = false;
  homesLoadError = '';
  homesPanelOpen = false;
  homeSearchText = '';
  homesPanelStyle: { [key: string]: string } = {};
  /** Composite key: "{pharmacyId}:{facilityId}" */
  selectedHomeKey: string | null = null;
  selectedPharmacyId: number | null = null;
  selectedHomeId: number | null = null;

  unitOptions: { id: number; name: string }[] = [];
  selectedUnitIds: number[] = [];
  unitsLoading = false;
  unitsLoadError = '';
  unitsPanelOpen = false;
  unitsPanelStyle: { [key: string]: string } = {};

  @ViewChild('homesWrap', { static: false })
  homesWrap: ElementRef;

  @ViewChild('homesTrigger', { static: false })
  homesTrigger: ElementRef;

  @ViewChild('homeSearchInput', { static: false })
  homeSearchInput: ElementRef;

  @ViewChild('homesPanel', { static: false })
  homesPanel: ElementRef;

  @ViewChild('unitsWrap', { static: false })
  unitsWrap: ElementRef;

  @ViewChild('unitsTrigger', { static: false })
  unitsTrigger: ElementRef;

  drugCategories: DrugStatisticsCategory[] = [
    { id: 'antipsychotics', label: 'Antipsychotics', selected: true, groupId: 6 },
    { id: 'benzodiazepines', label: 'Benzodiazepines', selected: true, groupId: 5 },
    { id: 'antidepressants', label: 'Antidepressants', selected: true, groupId: 11 },
    { id: 'trazodone', label: 'Trazodone', selected: true, groupId: 9 },
    { id: 'narcotics', label: 'Narcotics', selected: true, groupId: 8 },
    { id: 'antibiotics', label: 'Antibiotics', selected: true, groupId: 7 }
  ];

  /** Calendar quarter the user selects as Report Period (only quarters present in StatisticsRx). */
  periodYear = 0;
  periodQuarter = 0;
  periodYearOptions: number[] = [];
  periodQuarterOptions: number[] = [];
  periodsLoading = false;
  periodsLoadError = '';
  availableReportMonths: Date[] = [];
  availableReportQuarters: { year: number; quarter: number }[] = [];
  periodDateRangeLabel = '';
  /** Data-window label: selected quarter plus the two before it. */
  reportPeriodLabel = '';
  /** API window end = the selected Report Period quarter. */
  reportYear = 0;
  reportQuarter = 0;
  licensedBeds: number | null = null;
  /** Snapshot of total beds from Connect (for Reset). */
  defaultLicensedBedsTotal: number | null = null;
  /** Active patient total from Connect (sum of ward beds at load). */
  activePatientCount: number | null = null;
  /** Per-ward beds used as Average Rx denominators. */
  wardBedsEditors: WardBedsEditor[] = [];

  loading = false;
  reportError = '';
  reportQuarters: MedicationClassReportQuarter[] = [];
  /** Full medication-class payload (before Drug Categories filter). */
  allReportRows: MedicationClassReportRow[] = [];
  /** Filtered by selected Drug Categories. */
  reportRows: MedicationClassReportRow[] = [];
  reportVisible = false;

  selectedHomeName = '';
  averageRxQuarters: MedicationClassReportQuarter[] = [];
  averageRxRows: AverageRxReportRow[] = [];
  averageRxVisible = false;
  bedsPerWard: number | null = null;

  reportDateLabel = '';
  durFacilityNotes: string[] = [];

  allAcpCategoryRows: AcpCategoryAverageRow[] = [];
  visibleAcpCategoryRows: AcpCategoryAverageRow[] = [];
  acpCategoryVisible = false;

  acpCategoryTrends: { groupId: number; label: string; pct: number }[] = [];
  private drugGroupTrendQuarters: string[] = [];
  private drugGroupFacilityTrends: { groupId: number; label: string; pct: number; totalPatients: number }[] = [];

  trendChartWidth = 880;
  trendChartHeight = 320;
  trendChartPlot = { left: 56, top: 10, right: 24, bottom: 48 };
  trendCharts: TrendChartView[] = [];
  trendChartVisible = false;

  incidentTrendCharts: IncidentTrendChartView[] = [];
  incidentTrendVisible = false;
  private incidentTrendQuarters: string[] = [];
  private incidentTrendTotals: number[] = [];
  private incidentOriginSeries: { title: string; values: number[] }[] = [];
  private incidentOutcomeSeries: { title: string; values: number[] }[] = [];
  private incidentTypeSeries: { title: string; values: number[] }[] = [];
  private incidentRouteSeries: { title: string; values: number[] }[] = [];
  private incidentTableOpenByKey: { [key: string]: boolean } = {};
  private readonly incidentChartColors = [
    '#2e86c1', '#e67e22', '#27ae60', '#8e44ad', '#c0392b',
    '#16a085', '#f4d03f', '#5d6d7e', '#d35400', '#1abc9c',
    '#7d3c98', '#2874a6'
  ];

  constructor(
    private sharedService: SharedService,
    private statisticsService: StatisticsService
  ) { }

  ngOnInit(): void {
    this.reportDateLabel = this.buildReportDateLabel();
    this.loadAvailableReportPeriods();
    this.loadHomeGroups();
  }

  homeOptionValue(pharmacyId: number, facilityId: number): string {
    return pharmacyId + ':' + facilityId;
  }

  isHomeSelected(): boolean {
    return this.selectedHomeId != null
      && this.selectedHomeId > 0
      && this.selectedPharmacyId != null
      && this.selectedPharmacyId > 0;
  }

  onHomeChange(): void {
    this.homesPanelOpen = false;
    this.homeSearchText = '';
    this.unitsPanelOpen = false;
    this.resetUnits();
    this.clearReport();
    this.parseSelectedHomeKey();
    this.updateSelectedHomeName();
    this.refreshVisibleHomeGroups();
    if (!this.isHomeSelected()) {
      return;
    }
    this.loadUnitsForHome(this.selectedPharmacyId, this.selectedHomeId);
  }

  homesTriggerText(): string {
    if (this.homesLoading) {
      return 'Loading homes...';
    }
    if (this.selectedHomeName) {
      return this.selectedHomeName;
    }
    return this.selectHomePlaceholder;
  }

  onHomeSearchChange(): void {
    this.refreshVisibleHomeGroups();
  }

  onOrganizationChange(): void {
    this.homesPanelOpen = false;
    this.homeSearchText = '';
    this.refreshVisibleHomeGroups();
    if (!this.isHomeSelected()) {
      return;
    }
    if (this.selectedOrganizationPharmacyId > 0
        && this.selectedPharmacyId !== this.selectedOrganizationPharmacyId) {
      this.selectedHomeKey = null;
      this.onHomeChange();
    }
  }

  toggleHomesPanel(ev?: Event): void {
    if (ev && ev.stopPropagation) {
      ev.stopPropagation();
    }
    if (this.homesLoading) {
      return;
    }
    this.homesPanelOpen = !this.homesPanelOpen;
    if (this.homesPanelOpen) {
      this.unitsPanelOpen = false;
      this.refreshVisibleHomeGroups();
      var self = this;
      window.setTimeout(function () {
        self.updateHomesPanelPosition();
        var input = self.homeSearchInput && self.homeSearchInput.nativeElement;
        if (input && input.focus) {
          input.focus();
        }
      }, 0);
    }
  }

  selectHome(pharmacyId: number, home: DrugStatisticsHomeOption, ev?: Event): void {
    if (ev && ev.preventDefault) {
      ev.preventDefault();
    }
    if (ev && ev.stopPropagation) {
      ev.stopPropagation();
    }
    if (!home) {
      return;
    }
    this.selectedHomeKey = this.homeOptionValue(pharmacyId, home.id);
    this.onHomeChange();
  }

  isHomeOptionSelected(pharmacyId: number, facilityId: number): boolean {
    return this.selectedHomeKey === this.homeOptionValue(pharmacyId, facilityId);
  }

  private refreshVisibleHomeGroups(): void {
    this.visibleHomeGroups = this.buildVisibleHomeGroups();
  }

  private buildVisibleHomeGroups(): DrugStatisticsHomeGroup[] {
    var query = (this.homeSearchText || '').toLowerCase();
    query = query.replace(/^\s+|\s+$/g, '');
    var result: DrugStatisticsHomeGroup[] = [];
    for (var g = 0; g < this.homeGroups.length; g++) {
      var group = this.homeGroups[g];
      if (this.selectedOrganizationPharmacyId > 0
        && group.pharmacyId !== this.selectedOrganizationPharmacyId) {
        continue;
      }
      var homes: DrugStatisticsHomeOption[] = [];
      for (var h = 0; h < group.homes.length; h++) {
        var home = group.homes[h];
        if (!query
          || (home.name || '').toLowerCase().indexOf(query) >= 0
          || (group.pharmacyName || '').toLowerCase().indexOf(query) >= 0) {
          homes.push(home);
        }
      }
      if (homes.length > 0) {
        result.push({
          pharmacyId: group.pharmacyId,
          pharmacyName: group.pharmacyName,
          homes: homes
        });
      }
    }
    return result;
  }

  unitsSummaryText(): string {
    if (!this.isHomeSelected()) {
      return this.selectUnitPlaceholder;
    }
    if (this.unitsLoading) {
      return 'Loading units...';
    }
    if (this.unitsLoadError) {
      return 'Failed to load units';
    }
    if (!this.unitOptions || this.unitOptions.length === 0) {
      return 'No units available';
    }
    var n = this.selectedUnitIds.length;
    if (n === 0) {
      return 'Choose one or more units...';
    }
    if (n === this.unitOptions.length) {
      return 'All units selected';
    }
    return n === 1 ? '1 unit selected' : (n + ' units selected');
  }

  isUnitSelected(id: number): boolean {
    return this.selectedUnitIds.indexOf(id) >= 0;
  }

  toggleUnitsPanel(): void {
    if (!this.isHomeSelected() || this.unitsLoading) {
      this.unitsPanelOpen = false;
      return;
    }
    this.unitsPanelOpen = !this.unitsPanelOpen;
    if (this.unitsPanelOpen) {
      this.homesPanelOpen = false;
      var self = this;
      window.setTimeout(function () {
        self.updateUnitsPanelPosition();
      }, 0);
    }
  }

  toggleUnit(id: number, ev: Event): void {
    if (!this.isHomeSelected()) {
      return;
    }
    var t = ev.target as HTMLInputElement;
    if (!t) {
      return;
    }
    var checked = t.checked;
    var ids = this.selectedUnitIds.slice();
    var idx = ids.indexOf(id);
    if (checked && idx < 0) {
      ids.push(id);
    }
    if (!checked && idx >= 0) {
      ids.splice(idx, 1);
    }
    this.selectedUnitIds = ids;
  }

  selectAllUnits(): void {
    var ids: number[] = [];
    for (var i = 0; i < this.unitOptions.length; i++) {
      ids.push(this.unitOptions[i].id);
    }
    this.selectedUnitIds = ids;
  }

  clearUnits(): void {
    this.selectedUnitIds = [];
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent): void {
    var target = ev.target as Node;
    if (this.unitsPanelOpen) {
      var unitsWrap = this.unitsWrap && this.unitsWrap.nativeElement;
      if (!(unitsWrap && target && unitsWrap.contains(target))) {
        this.unitsPanelOpen = false;
      }
    }
    if (this.homesPanelOpen) {
      var homesWrap = this.homesWrap && this.homesWrap.nativeElement;
      var homesPanel = this.homesPanel && this.homesPanel.nativeElement;
      var inWrap = !!(homesWrap && target && homesWrap.contains(target));
      var inPanel = !!(homesPanel && target && homesPanel.contains(target));
      if (!inWrap && !inPanel) {
        this.homesPanelOpen = false;
      }
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.unitsPanelOpen) {
      this.updateUnitsPanelPosition();
    }
    if (this.homesPanelOpen) {
      this.updateHomesPanelPosition();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.unitsPanelOpen) {
      this.updateUnitsPanelPosition();
    }
    if (this.homesPanelOpen) {
      this.updateHomesPanelPosition();
    }
  }

  onPeriodYearChange(): void {
    this.refreshQuarterOptionsForYear();
    this.onReportPeriodChange();
  }

  onReportPeriodChange(): void {
    this.syncAnchorFromSelectedPeriod();
    this.clearReport();
  }

  selectAllCategories(): void {
    for (var i = 0; i < this.drugCategories.length; i++) {
      this.drugCategories[i].selected = true;
    }
    this.onCategorySelectionChange();
  }

  clearCategories(): void {
    for (var i = 0; i < this.drugCategories.length; i++) {
      this.drugCategories[i].selected = false;
    }
    this.onCategorySelectionChange();
  }

  onCategorySelectionChange(): void {
    this.applyCategoryFiltersToReport();
  }

  selectedCategoryCount(): number {
    var count = 0;
    for (var i = 0; i < this.drugCategories.length; i++) {
      if (this.drugCategories[i].selected) {
        count++;
      }
    }
    return count;
  }

  canGenerate(): boolean {
    return this.isHomeSelected()
      && this.selectedCategoryCount() > 0
      && this.selectedUnitIds.length > 0
      && this.isAvailableReportPeriod()
      && !this.loading
      && !this.homesLoading
      && !this.unitsLoading
      && !this.periodsLoading;
  }

  private syncLicensedBedsFromWards(): void {
    var total = 0;
    for (var i = 0; i < this.wardBedsEditors.length; i++) {
      var beds = this.wardBedsEditors[i].licensedBeds;
      if (beds != null && beds > 0) {
        total += beds;
      }
    }
    this.licensedBeds = total > 0 ? total : null;
  }

  generateReport(): void {
    if (!this.canGenerate()) {
      return;
    }

    this.loading = true;
    this.reportError = '';
    this.unitsPanelOpen = false;
    this.homesPanelOpen = false;
    this.updateSelectedHomeName();

    var wardIds: number[] = undefined;
    if (this.selectedUnitIds.length > 0
      && this.selectedUnitIds.length < this.unitOptions.length) {
      wardIds = this.selectedUnitIds.slice();
    }
    var groupIds = this.getSelectedGroupIds();
    var self = this;

    this.statisticsService.getDrugStatisticsFromRx(
      this.selectedPharmacyId,
      this.selectedHomeId,
      this.reportYear,
      this.reportQuarter,
      wardIds,
      groupIds
    ).subscribe(
      function (data: any) {
        self.loading = false;
        if (!data) {
          self.reportError = 'No report data returned.';
          self.clearReport();
          return;
        }

        var classData = data.medicationClass;
        var avgRxData = data.averageRx;
        var acpData = data.acpAverages;
        var hasClass = classData && classData.rows;
        var hasAvg = avgRxData && avgRxData.rows;
        if (!hasClass && !hasAvg) {
          self.reportError = 'No StatisticsRx rows for this home and data window.';
          self.clearReport();
          return;
        }
        if (hasAvg) {
          self.applyAverageRxPayload(avgRxData);
        } else {
          self.averageRxVisible = false;
          self.averageRxRows = [];
          self.averageRxQuarters = [];
        }
        if (hasClass) {
          self.applyReportPayload(classData);
        } else {
          self.reportVisible = false;
          self.reportRows = [];
          self.reportQuarters = [];
        }
        self.applyAcpCategoryPayload(acpData);
        self.applyDrugGroupTrendsPayload(data.drugGroupTrends);
        self.applyIncidentTrendsPayload(data.incidentTrends);
        self.buildDurFacilityNotes();
        self.applyCategoryFiltersToReport();
        self.buildIncidentTrendCharts();
      },
      function () {
        self.loading = false;
        self.reportError = 'Unable to generate the report. Please try again.';
        self.clearReport();
      }
    );
  }

  exportPdf(): void {
    var contentEl = document.getElementById('misc-drug-stats-print-content');
    if (!contentEl) {
      this.reportError = 'Unable to export. The report was not found on the page.';
      return;
    }

    var printWindow = window.open('', '_blank');
    if (!printWindow) {
      this.reportError = 'Popup blocked. Please allow popups to export the PDF.';
      return;
    }

    var cloned = contentEl.cloneNode(true) as HTMLElement;
    this.removeIncidentEditorsFromClone(cloned);
    this.absolutizeImageSources(cloned);

    var styles = this.collectPageStyles();
    var title = this.buildPdfTitle();
    printWindow.document.open();
    printWindow.document.write(
      '<!doctype html><html><head><meta charset="utf-8" />'
      + '<title>' + this.escapeHtml(title) + '</title>'
      + styles
      + '<style>'
      + this.getPdfPrintCss()
      + '</style></head><body>'
      + cloned.outerHTML
      + '</body></html>'
    );
    printWindow.document.close();
    printWindow.document.title = title;

    setTimeout(function () {
      try {
        printWindow.focus();
        printWindow.print();
      } catch (_e) { }
    }, 400);
  }

  isDrugTrendPrintBreak(index: number): boolean {
    return index > 0 && index % 2 === 0;
  }

  isIncidentSectionPrintBreak(): boolean {
    var n = this.trendCharts && this.trendCharts.length ? this.trendCharts.length : 0;
    return n % 2 === 0;
  }

  isIncidentTrendPrintBreak(index: number): boolean {
    var n = this.trendCharts && this.trendCharts.length ? this.trendCharts.length : 0;
    var globalIndex = n + index;
    if (globalIndex % 2 !== 0) {
      return false;
    }
    if (index === 0 && n % 2 === 0) {
      return false;
    }
    return true;
  }

  private getPdfPrintCss(): string {
    return ''
      + '@page { size: letter portrait; margin: 0 12mm 12mm 12mm; }'
      + '@page {'
      + '@top-left { content: none; }'
      + '@top-center { content: none; }'
      + '@top-right { content: none; }'
      + '@bottom-left { content: none; }'
      + '@bottom-right { content: none; }'
      + '@bottom-center { content: counter(page); font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #212529; }'
      + '}'
      + 'html, body { margin: 0; background: #fff; }'
      + 'body { padding-top: 60mm; padding-bottom: 4mm; }'
      + '.misc-drug-stats-incident-editor,'
      + '.misc-drug-stats-print-hide,'
      + '.misc-drug-stats-print-page-footer { display: none !important; }'
      + '.misc-drug-stats-pac-doc { box-shadow: none; border: none; }'
      + '.misc-drug-stats-print-logo-header {'
      + 'display: block; position: fixed; top: 0; left: 0; right: 0;'
      + 'height: 56mm; padding: 10mm 12mm 6mm 12mm; box-sizing: border-box;'
      + 'text-align: center; background: #fff; z-index: 9999;'
      + '}'
      + '.misc-drug-stats-print-logo-header-img {'
      + 'display: inline-block; height: 40mm; width: auto; max-width: 140mm; object-fit: contain;'
      + '}'
      + '.misc-drug-stats-acp-logo-wrap { display: none !important; }'
      + '.misc-drug-stats-pac-header { padding-top: 0.25rem; }'
      + '.misc-drug-stats-print-acp-heading { margin-top: 10mm !important; padding-top: 0; }'
      + '.misc-drug-stats-print-break {'
      + 'page-break-before: always; break-before: page; padding-top: 60mm;'
      + '}'
      + '.misc-drug-stats-print-break.misc-drug-stats-pac-section-title,'
      + '.misc-drug-stats-print-break .misc-drug-stats-pac-section-title { margin-top: 0; }'
      + '.misc-drug-stats-trend { page-break-inside: avoid; break-inside: avoid; margin-bottom: 0.35rem; }'
      + '.misc-drug-stats-trend-chart-wrap,'
      + '.misc-drug-stats-trend-chart-scroll {'
      + 'width: 100% !important; max-width: 100% !important; overflow: visible !important;'
      + '}'
      + '.misc-drug-stats-trend-svg {'
      + 'display: block; width: 100% !important; min-width: 0 !important;'
      + 'max-width: 100% !important; height: auto !important; max-height: none !important;'
      + '}'
      + '.misc-drug-stats-pac-section-title { font-size: 1.15rem; margin-bottom: 0.4rem; }'
      + '.misc-drug-stats-trend-legend { font-size: 0.7rem; }'
      + 'svg { max-width: 100%; height: auto; }'
      + '* { -webkit-print-color-adjust: exact; print-color-adjust: exact; }';
  }

  private removeIncidentEditorsFromClone(root: HTMLElement): void {
    var editors = root.querySelectorAll('.misc-drug-stats-incident-editor');
    for (var i = 0; i < editors.length; i++) {
      var node = editors[i];
      if (node && node.parentNode) {
        node.parentNode.removeChild(node);
      }
    }
  }

  private absolutizeImageSources(root: HTMLElement): void {
    var imgs = root.getElementsByTagName('img');
    for (var i = 0; i < imgs.length; i++) {
      imgs[i].src = imgs[i].src;
    }
  }

  private collectPageStyles(): string {
    var html = '';
    var nodes = document.querySelectorAll('style, link[rel="stylesheet"]');
    for (var i = 0; i < nodes.length; i++) {
      html += nodes[i].outerHTML;
    }
    return html;
  }

  private buildPdfTitle(): string {
    var home = this.selectedHomeName ? this.selectedHomeName : 'Facility';
    var period = '';
    if (this.periodQuarter && this.periodYear) {
      period = ' Q' + this.periodQuarter + ' ' + this.periodYear;
    }
    return 'PAC Report - ' + home + period;
  }

  private escapeHtml(text: string): string {
    return String(text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  formatPct(pct: number): string {
    if (pct == null) {
      return '0.0';
    }
    var rounded = Math.round(pct * 10) / 10;
    var text = String(rounded);
    if (text.indexOf('.') < 0) {
      text = text + '.0';
    }
    return text;
  }

  formatRate(rate: number): string {
    if (rate == null) {
      return '0.00';
    }
    var rounded = Math.round(rate * 100) / 100;
    var text = String(rounded);
    if (text.indexOf('.') < 0) {
      return text + '.00';
    }
    var parts = text.split('.');
    if (parts[1].length === 1) {
      return text + '0';
    }
    return text;
  }

  private applyAverageRxPayload(data: any): void {
    this.applyLicensedBedsFromResponse(data);
    this.applyWardBedsFromResponse(data);

    var quarters: MedicationClassReportQuarter[] = [];
    var rawQuarters = data.quarters || [];
    for (var q = 0; q < rawQuarters.length; q++) {
      quarters.push({
        label: rawQuarters[q].label,
        sort: rawQuarters[q].sort
      });
    }
    this.averageRxQuarters = quarters;

    var rows: AverageRxReportRow[] = [];
    var rawRows = data.rows || [];
    for (var i = 0; i < rawRows.length; i++) {
      var raw = rawRows[i];
      var cells: AverageRxReportCell[] = [];
      var rawCells = raw.cells || [];
      for (var c = 0; c < rawCells.length; c++) {
        var cell = rawCells[c];
        cells.push({
          quarterSort: cell.quarterSort,
          quarterLabel: cell.quarterLabel,
          regRate: cell.regRate || 0,
          prnRate: cell.prnRate || 0,
          txRate: cell.txRate || 0,
          totalRate: cell.totalRate || 0,
          regRxCount: cell.regRxCount || 0,
          prnRxCount: cell.prnRxCount || 0,
          txRxCount: cell.txRxCount || 0
        });
      }
      rows.push({
        wardId: raw.wardId != null ? raw.wardId : null,
        wardName: raw.wardName,
        isTotal: !!raw.isTotal,
        sortOrder: raw.sortOrder,
        licensedBeds: raw.licensedBeds != null ? raw.licensedBeds : 0,
        cells: cells
      });
    }
    rows.sort(function (a, b) {
      if (a.isTotal && !b.isTotal) {
        return -1;
      }
      if (!a.isTotal && b.isTotal) {
        return 1;
      }
      var nameA = (a.wardName || '').toLowerCase();
      var nameB = (b.wardName || '').toLowerCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });
    this.averageRxRows = rows;
    this.averageRxVisible = rows.length > 0;
    this.recalculateAverageRxRates();
  }

  private applyWardBedsFromResponse(data: any): void {
    var editors: WardBedsEditor[] = [];
    var wards = data && data.wards ? data.wards : [];
    for (var i = 0; i < wards.length; i++) {
      var w = wards[i];
      if (w == null || w.wardId == null) {
        continue;
      }
      var beds = w.licensedBeds != null && w.licensedBeds > 0 ? w.licensedBeds : 1;
      editors.push({
        wardId: w.wardId,
        wardName: w.wardName || ('Ward ' + w.wardId),
        licensedBeds: beds,
        defaultLicensedBeds: beds
      });
    }
    this.wardBedsEditors = editors;
    this.syncLicensedBedsFromWards();
    this.defaultLicensedBedsTotal = this.licensedBeds;
    if (this.licensedBeds != null) {
      this.activePatientCount = this.licensedBeds;
    }
  }

  private recalculateAverageRxRates(): void {
    var bedsByWard: { [wardId: number]: number } = {};
    for (var i = 0; i < this.wardBedsEditors.length; i++) {
      bedsByWard[this.wardBedsEditors[i].wardId] = this.wardBedsEditors[i].licensedBeds;
    }

    var totalBeds = this.licensedBeds != null && this.licensedBeds > 0
      ? this.licensedBeds
      : 0;

    for (var r = 0; r < this.averageRxRows.length; r++) {
      var row = this.averageRxRows[r];
      var denom = totalBeds;
      if (!row.isTotal && row.wardId != null && bedsByWard[row.wardId] != null) {
        denom = bedsByWard[row.wardId];
        row.licensedBeds = denom;
      } else if (row.isTotal) {
        row.licensedBeds = totalBeds;
      }
      for (var c = 0; c < row.cells.length; c++) {
        var cell = row.cells[c];
        cell.regRate = this.calcRate(cell.regRxCount, denom);
        cell.prnRate = this.calcRate(cell.prnRxCount, denom);
        cell.txRate = this.calcRate(cell.txRxCount, denom);
        cell.totalRate = Math.round((cell.regRate + cell.prnRate + cell.txRate) * 100) / 100;
      }
    }
  }

  private calcRate(count: number, beds: number): number {
    if (!beds || beds <= 0 || !count) {
      return 0;
    }
    return Math.round((count * 100) / beds) / 100;
  }

  private updateSelectedHomeName(): void {
    this.selectedHomeName = '';
    if (!this.selectedHomeKey || !this.homeGroups) {
      return;
    }
    for (var g = 0; g < this.homeGroups.length; g++) {
      var group = this.homeGroups[g];
      for (var h = 0; h < group.homes.length; h++) {
        var home = group.homes[h];
        if (this.homeOptionValue(group.pharmacyId, home.id) === this.selectedHomeKey) {
          this.selectedHomeName = home.name;
          return;
        }
      }
    }
  }

  private applyReportPayload(data: any): void {
    this.applyLicensedBedsFromResponse(data);

    var quarters: MedicationClassReportQuarter[] = [];
    var rawQuarters = data.quarters || [];
    for (var q = 0; q < rawQuarters.length; q++) {
      quarters.push({
        label: rawQuarters[q].label,
        sort: rawQuarters[q].sort
      });
    }
    this.reportQuarters = quarters;

    var rows: MedicationClassReportRow[] = [];
    var rawRows = data.rows || [];
    for (var i = 0; i < rawRows.length; i++) {
      var raw = rawRows[i];
      var cells: MedicationClassReportCell[] = [];
      var rawCells = raw.cells || [];
      for (var c = 0; c < rawCells.length; c++) {
        var cell = rawCells[c];
        cells.push({
          quarterSort: cell.quarterSort,
          quarterLabel: cell.quarterLabel,
          totalPatients: cell.totalPatients || 0,
          totalPct: cell.totalPct || 0,
          regPatients: cell.regPatients || 0,
          regPct: cell.regPct || 0,
          prnPatients: cell.prnPatients || 0,
          prnPct: cell.prnPct || 0
        });
      }
      rows.push({
        groupId: raw.groupId,
        medicationClass: raw.medicationClass,
        sortOrder: raw.sortOrder,
        cells: cells
      });
    }

    this.allReportRows = rows;
  }

  private applyLicensedBedsFromResponse(data: any): void {
    if (data == null || data.licensedBeds == null) {
      return;
    }
    // Prefer ward-sum when wards payload is applied; this covers medication-class-only responses.
    if (this.wardBedsEditors.length === 0) {
      this.activePatientCount = data.licensedBeds;
      if (this.licensedBeds == null || this.licensedBeds <= 0) {
        this.licensedBeds = data.licensedBeds;
      }
    }
  }

  private applyCategoryFiltersToReport(): void {
    var selectedGroupIds = this.getSelectedGroupIds();

    // Display order/labels are owned by Portal (drugCategories).
    var medRows: MedicationClassReportRow[] = [];
    for (var i = 0; i < selectedGroupIds.length; i++) {
      var groupId = selectedGroupIds[i];
      var found: MedicationClassReportRow = null;
      for (var r = 0; r < this.allReportRows.length; r++) {
        if (this.allReportRows[r].groupId === groupId) {
          found = this.allReportRows[r];
          break;
        }
      }
      if (found) {
        found.medicationClass = this.getCategoryLabelForGroupId(groupId);
        found.sortOrder = i + 1;
        medRows.push(found);
      } else {
        medRows.push(this.createEmptyMedicationClassRow(groupId, i + 1));
      }
    }
    this.reportRows = medRows;
    this.reportVisible = medRows.length > 0 && this.reportQuarters.length > 0;

    var acpRows: AcpCategoryAverageRow[] = [];
    for (var s = 0; s < selectedGroupIds.length; s++) {
      var acpGroupId = selectedGroupIds[s];
      var foundAcp: AcpCategoryAverageRow = null;
      for (var a = 0; a < this.allAcpCategoryRows.length; a++) {
        var acp = this.allAcpCategoryRows[a];
        if (acp.groupId != null && acp.groupId === acpGroupId) {
          foundAcp = acp;
          break;
        }
      }
      if (foundAcp) {
        foundAcp.category = this.getCategoryLabelForGroupId(acpGroupId);
        acpRows.push(foundAcp);
      } else {
        acpRows.push({
          key: 'group-' + acpGroupId,
          category: this.getCategoryLabelForGroupId(acpGroupId),
          value: '0%',
          groupId: acpGroupId,
          pct: 0,
          numerator: 0,
          denominator: 0
        });
      }
    }
    if (selectedGroupIds.length > 0) {
      for (var d = 0; d < this.allAcpCategoryRows.length; d++) {
        if (this.allAcpCategoryRows[d].key === 'durAverage') {
          acpRows.push(this.allAcpCategoryRows[d]);
          break;
        }
      }
    }
    this.visibleAcpCategoryRows = acpRows;
    this.acpCategoryVisible = acpRows.length > 0;

    this.recalculatePercentages();
    this.buildTrendCharts();

    if ((this.allReportRows.length > 0 || this.allAcpCategoryRows.length > 0)
      && !this.reportVisible
      && !this.acpCategoryVisible
      && !this.trendChartVisible
      && !this.averageRxVisible) {
      this.reportError = 'No report sections match the selected drug categories.';
    } else if (this.reportVisible || this.acpCategoryVisible || this.averageRxVisible) {
      this.reportError = '';
    }
  }

  private createEmptyMedicationClassRow(groupId: number, sortOrder: number): MedicationClassReportRow {
    var cells: MedicationClassReportCell[] = [];
    for (var q = 0; q < this.reportQuarters.length; q++) {
      cells.push({
        quarterSort: this.reportQuarters[q].sort,
        quarterLabel: this.reportQuarters[q].label,
        totalPatients: 0,
        totalPct: 0,
        regPatients: 0,
        regPct: 0,
        prnPatients: 0,
        prnPct: 0
      });
    }
    return {
      groupId: groupId,
      medicationClass: this.getCategoryLabelForGroupId(groupId),
      sortOrder: sortOrder,
      cells: cells
    };
  }

  private hasAnySelectedAcpCategory(): boolean {
    return this.getSelectedGroupIds().length > 0;
  }

  private getSelectedGroupIds(): number[] {
    var ids: number[] = [];
    for (var i = 0; i < this.drugCategories.length; i++) {
      var cat = this.drugCategories[i];
      if (!cat.selected || cat.groupId == null) {
        continue;
      }
      if (ids.indexOf(cat.groupId) < 0) {
        ids.push(cat.groupId);
      }
    }
    return this.sortGroupIdsByDisplayOrder(ids);
  }

  /** Same order as Medication Class / ACP Drug Category / Drug Categories checklist. */
  private sortGroupIdsByDisplayOrder(ids: number[]): number[] {
    var order = [6, 5, 11, 9, 8, 7];
    return ids.slice().sort(function (a, b) {
      var ia = order.indexOf(a);
      var ib = order.indexOf(b);
      if (ia < 0) {
        ia = 999;
      }
      if (ib < 0) {
        ib = 999;
      }
      return ia - ib;
    });
  }

  private getCategoryLabelForGroupId(groupId: number): string {
    for (var i = 0; i < this.drugCategories.length; i++) {
      if (this.drugCategories[i].groupId === groupId) {
        return this.drugCategories[i].label;
      }
    }
    return 'Drug Category';
  }

  private recalculatePercentages(): void {
    var beds = this.licensedBeds != null && this.licensedBeds > 0
      ? this.licensedBeds
      : 0;
    for (var i = 0; i < this.allReportRows.length; i++) {
      var row = this.allReportRows[i];
      for (var c = 0; c < row.cells.length; c++) {
        var cell = row.cells[c];
        cell.totalPct = this.calcPct(cell.totalPatients, beds);
        cell.regPct = this.calcPct(cell.regPatients, beds);
        cell.prnPct = this.calcPct(cell.prnPatients, beds);
      }
    }
    for (var t = 0; t < this.drugGroupFacilityTrends.length; t++) {
      this.drugGroupFacilityTrends[t].pct = this.calcPct(
        this.drugGroupFacilityTrends[t].totalPatients,
        beds
      );
    }
  }

  private calcPct(count: number, beds: number): number {
    if (!beds || beds <= 0 || !count) {
      return 0;
    }
    return Math.round((count * 1000) / beds) / 10;
  }

  private clearReport(): void {
    this.reportVisible = false;
    this.allReportRows = [];
    this.reportRows = [];
    this.reportQuarters = [];
    this.averageRxVisible = false;
    this.averageRxRows = [];
    this.averageRxQuarters = [];
    this.wardBedsEditors = [];
    this.defaultLicensedBedsTotal = null;
    this.bedsPerWard = null;
    this.trendChartVisible = false;
    this.trendCharts = [];
    this.durFacilityNotes = [];
    this.acpCategoryTrends = [];
    this.drugGroupTrendQuarters = [];
    this.drugGroupFacilityTrends = [];
    this.allAcpCategoryRows = [];
    this.visibleAcpCategoryRows = [];
    this.acpCategoryVisible = false;
    this.incidentTrendCharts = [];
    this.incidentTrendVisible = false;
    this.incidentTrendQuarters = [];
    this.incidentTrendTotals = [];
    this.incidentOriginSeries = [];
    this.incidentOutcomeSeries = [];
    this.incidentTypeSeries = [];
    this.incidentRouteSeries = [];
    this.incidentTableOpenByKey = {};
  }

  private applyAcpCategoryPayload(data: any): void {
    this.acpCategoryTrends = [];
    this.allAcpCategoryRows = [];
    if (!data) {
      return;
    }

    var categories = data.categories || [];
    for (var c = 0; c < categories.length; c++) {
      var item = categories[c];
      var key = item.key;
      var groupId = item.groupId != null ? item.groupId : null;
      if (key === 'durAverage') {
        groupId = null;
      }
      this.allAcpCategoryRows.push({
        key: key,
        category: item.label || key,
        value: item.valueText || '',
        groupId: groupId,
        pct: item.pct != null ? item.pct : 0,
        numerator: item.numerator != null ? item.numerator : null,
        denominator: item.denominator != null ? item.denominator : null
      });
    }

    var trend = data.categoryTrends || data.antipsychoticTrend || [];
    for (var t = 0; t < trend.length; t++) {
      var point = trend[t];
      if (point == null || point.groupId == null) {
        continue;
      }
      this.acpCategoryTrends.push({
        groupId: point.groupId,
        label: point.quarterLabel,
        pct: point.pct || 0
      });
    }
  }

  private applyDrugGroupTrendsPayload(data: any): void {
    this.drugGroupTrendQuarters = [];
    this.drugGroupFacilityTrends = [];
    if (!data) {
      return;
    }

    var quarters = data.quarters || [];
    for (var q = 0; q < quarters.length; q++) {
      var label = quarters[q] && quarters[q].label ? String(quarters[q].label) : '';
      if (label) {
        this.drugGroupTrendQuarters.push(label);
      }
    }

    var rows = data.facilityTrends || [];
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (!row || row.groupId == null) {
        continue;
      }
      this.drugGroupFacilityTrends.push({
        groupId: row.groupId,
        label: row.quarterLabel ? String(row.quarterLabel) : '',
        pct: row.pct != null ? row.pct : 0,
        totalPatients: row.totalPatients != null ? row.totalPatients : 0
      });
    }
  }

  private applyIncidentTrendsPayload(data: any): void {
    this.incidentTrendQuarters = [];
    this.incidentTrendTotals = [];
    this.incidentOriginSeries = [];
    this.incidentOutcomeSeries = [];
    this.incidentTypeSeries = [];
    this.incidentRouteSeries = [];
    if (!data) {
      return;
    }

    var quarters = data.quarters || [];
    for (var q = 0; q < quarters.length; q++) {
      var label = quarters[q] && quarters[q].label ? String(quarters[q].label) : '';
      if (label) {
        this.incidentTrendQuarters.push(label);
      }
    }

    var totals = data.totals || [];
    for (var t = 0; t < this.incidentTrendQuarters.length; t++) {
      this.incidentTrendTotals.push(this.findIncidentCount(totals, this.incidentTrendQuarters[t]));
    }

    this.incidentOriginSeries = this.mapIncidentSeries(data.origins, this.incidentTrendQuarters);
    this.incidentOutcomeSeries = this.mapIncidentSeries(data.outcomes, this.incidentTrendQuarters);
    this.incidentTypeSeries = this.mapIncidentSeries(data.incidentTypes, this.incidentTrendQuarters);
    this.incidentRouteSeries = this.mapIncidentSeries(data.routeAdministrations, this.incidentTrendQuarters);
    this.shortenIncidentOutcomeTitles();
  }

  private shortenIncidentOutcomeTitles(): void {
    for (var i = 0; i < this.incidentOutcomeSeries.length; i++) {
      this.incidentOutcomeSeries[i].title = this.shortenOutcomeTitle(this.incidentOutcomeSeries[i].title);
    }
  }

  private shortenOutcomeTitle(title: string): string {
    if (!title) {
      return '';
    }
    var separator = ' - ';
    var idx = title.indexOf(separator);
    if (idx > 0) {
      return title.substring(0, idx);
    }
    return title;
  }

  private mapIncidentSeries(raw: any[], quarterLabels: string[]): { title: string; values: number[] }[] {
    var result: { title: string; values: number[] }[] = [];
    var rows = raw || [];
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (!row) {
        continue;
      }
      var title = row.title ? String(row.title) : ('Type ' + row.typeId);
      var values: number[] = [];
      var points = row.points || [];
      for (var q = 0; q < quarterLabels.length; q++) {
        values.push(this.findIncidentCount(points, quarterLabels[q]));
      }
      result.push({ title: title, values: values });
    }
    return result;
  }

  private findIncidentCount(points: any[], quarterLabel: string): number {
    if (!points) {
      return 0;
    }
    for (var i = 0; i < points.length; i++) {
      var p = points[i];
      if (p && p.quarterLabel === quarterLabel) {
        return p.count || 0;
      }
    }
    return 0;
  }

  toggleIncidentTable(chart: IncidentTrendChartView): void {
    if (!chart || !chart.editorKey) {
      return;
    }
    var next = !this.isIncidentTableOpen(chart.editorKey);
    this.incidentTableOpenByKey[chart.editorKey] = next;
    chart.tableOpen = next;
  }

  incidentTableToggleLabel(chart: IncidentTrendChartView): string {
    if (chart && chart.tableOpen) {
      return 'Hide values';
    }
    return 'Show values';
  }

  incidentTableCellValue(row: IncidentTrendEditorRow, colIndex: number): number {
    if (!row || !row.values || colIndex < 0 || colIndex >= row.values.length) {
      return 0;
    }
    var n = row.values[colIndex];
    return n != null ? n : 0;
  }

  onIncidentEditorValueChange(
    chart: IncidentTrendChartView,
    rowIndex: number,
    colIndex: number,
    raw: any
  ): void {
    if (!chart || !chart.tableRows || rowIndex < 0 || rowIndex >= chart.tableRows.length) {
      return;
    }
    var row = chart.tableRows[rowIndex];
    if (!row || !row.values || colIndex < 0 || colIndex >= row.values.length) {
      return;
    }
    var n = parseInt(String(raw), 10);
    if (isNaN(n) || n < 0) {
      n = 0;
    }
    row.values[colIndex] = n;
    this.refreshIncidentChartFromEditor(chart);
  }

  private isIncidentTableOpen(editorKey: string): boolean {
    return !!(editorKey && this.incidentTableOpenByKey[editorKey]);
  }

  private incidentEditorTableRows(typeSeries: { title: string; values: number[] }[]): IncidentTrendEditorRow[] {
    var rows: IncidentTrendEditorRow[] = [];
    var list = typeSeries || [];
    for (var i = 0; i < list.length; i++) {
      rows.push({
        label: list[i].title,
        values: list[i].values
      });
    }
    rows.sort(function (a, b) {
      var la = (a.label || '').toLowerCase();
      var lb = (b.label || '').toLowerCase();
      if (la < lb) {
        return -1;
      }
      if (la > lb) {
        return 1;
      }
      return 0;
    });
    return rows;
  }

  private refreshIncidentChartFromEditor(chart: IncidentTrendChartView): void {
    if (!chart) {
      return;
    }
    var rebuilt: IncidentTrendChartView;
    if (chart.editorKey === 'totals') {
      var totalValues = chart.tableRows.length ? chart.tableRows[0].values : [];
      rebuilt = this.buildIncidentCountChart(
        chart.title,
        this.incidentTrendQuarters,
        [{ title: 'Incidents', values: totalValues }],
        false,
        chart.editorKey,
        chart.tableRows
      );
    } else {
      var typeSeries: { title: string; values: number[] }[] = [];
      for (var i = 0; i < chart.tableRows.length; i++) {
        typeSeries.push({
          title: chart.tableRows[i].label,
          values: chart.tableRows[i].values
        });
      }
      rebuilt = this.buildIncidentTypeByQuarterChart(
        chart.title,
        typeSeries,
        chart.editorKey,
        chart.tableRows
      );
    }
    chart.bars = rebuilt.bars;
    chart.labels = rebuilt.labels;
    chart.yLabels = rebuilt.yLabels;
    chart.legendItems = rebuilt.legendItems;
    chart.emptyMessage = rebuilt.emptyMessage;
    chart.viewWidth = rebuilt.viewWidth;
    chart.viewHeight = rebuilt.viewHeight;
    chart.plotLeft = rebuilt.plotLeft;
    chart.plotBottom = rebuilt.plotBottom;
    chart.rotateXLabels = rebuilt.rotateXLabels;
    chart.tableOpen = true;
    this.incidentTableOpenByKey[chart.editorKey] = true;
  }

  private buildIncidentTrendCharts(): void {
    this.incidentTrendCharts = [];
    var homeName = this.selectedHomeName ? this.selectedHomeName : 'Facility';
    var periodLabel = this.incidentTrendPeriodLabel();
    if (!this.incidentTrendQuarters.length) {
      this.incidentTrendVisible = false;
      return;
    }

    this.incidentTrendCharts.push(this.buildIncidentCountChart(
      homeName + ' - Total Incidents Reported ' + periodLabel,
      this.incidentTrendQuarters,
      [{ title: 'Incidents', values: this.incidentTrendTotals }],
      false,
      'totals',
      [{ label: 'Incidents', values: this.incidentTrendTotals }]
    ));
    this.incidentTrendCharts.push(this.buildIncidentOriginChart(homeName, periodLabel));
    this.incidentTrendCharts.push(this.buildIncidentOutcomeChart(homeName, periodLabel));
    this.incidentTrendCharts.push(this.buildIncidentRouteChart(homeName, periodLabel));
    this.incidentTrendCharts.push(this.buildIncidentTypeChart(homeName, periodLabel));
    this.incidentTrendVisible = true;
  }

  private incidentTrendPeriodLabel(): string {
    if (!this.reportYear || !this.reportQuarter) {
      return '';
    }
    return 'Q' + this.reportQuarter + ' ' + this.reportYear;
  }

  private buildIncidentOriginChart(homeName: string, periodLabel: string): IncidentTrendChartView {
    return this.buildIncidentTypeByQuarterChart(
      homeName + ' - Incident Origin ' + periodLabel,
      this.incidentOriginSeries,
      'origins',
      this.incidentEditorTableRows(this.incidentOriginSeries)
    );
  }

  private buildIncidentOutcomeChart(homeName: string, periodLabel: string): IncidentTrendChartView {
    return this.buildIncidentTypeByQuarterChart(
      homeName + ' - Resident Outcome ' + periodLabel,
      this.incidentOutcomeSeries,
      'outcomes',
      this.incidentEditorTableRows(this.incidentOutcomeSeries)
    );
  }

  private buildIncidentTypeChart(homeName: string, periodLabel: string): IncidentTrendChartView {
    return this.buildIncidentTypeByQuarterChart(
      homeName + ' - Incident Type ' + periodLabel,
      this.incidentTypeSeries,
      'types',
      this.incidentEditorTableRows(this.incidentTypeSeries)
    );
  }

  private buildIncidentRouteChart(homeName: string, periodLabel: string): IncidentTrendChartView {
    return this.buildIncidentTypeByQuarterChart(
      homeName + ' - Route of Administration ' + periodLabel,
      this.incidentRouteSeries,
      'routes',
      this.incidentEditorTableRows(this.incidentRouteSeries)
    );
  }

  private buildIncidentTypeByQuarterChart(
    title: string,
    typeSeries: { title: string; values: number[] }[],
    editorKey: string,
    tableRows: IncidentTrendEditorRow[]
  ): IncidentTrendChartView {
    var plotSeries = this.filterIncidentSeriesForChart(typeSeries);
    if (!plotSeries.length) {
      return this.buildIncidentCountChart(title, [], [], true, editorKey, tableRows || []);
    }
    var xLabels: string[] = [];
    for (var o = 0; o < plotSeries.length; o++) {
      xLabels.push(plotSeries[o].title);
    }
    var quarterSeries: { title: string; values: number[] }[] = [];
    for (var q = 0; q < this.incidentTrendQuarters.length; q++) {
      var values: number[] = [];
      for (var i = 0; i < plotSeries.length; i++) {
        var rowVals = plotSeries[i].values;
        var n = 0;
        if (rowVals && q < rowVals.length) {
          n = rowVals[q] || 0;
        }
        values.push(n);
      }
      quarterSeries.push({
        title: this.incidentTrendQuarters[q],
        values: values
      });
    }
    return this.buildIncidentCountChart(title, xLabels, quarterSeries, true, editorKey, tableRows);
  }

  private filterIncidentSeriesForChart(
    typeSeries: { title: string; values: number[] }[]
  ): { title: string; values: number[] }[] {
    var result: { title: string; values: number[] }[] = [];
    var list = typeSeries || [];
    for (var i = 0; i < list.length; i++) {
      if (this.incidentValuesHaveAnyCount(list[i].values)) {
        result.push(list[i]);
      }
    }
    return result;
  }

  private incidentValuesHaveAnyCount(values: number[]): boolean {
    if (!values) {
      return false;
    }
    for (var i = 0; i < values.length; i++) {
      if (values[i] != null && values[i] > 0) {
        return true;
      }
    }
    return false;
  }

  incidentAxisLabelTransform(chart: IncidentTrendChartView, lab: { x: number; text: string }): string {
    if (!chart || !chart.rotateXLabels || !lab) {
      return '';
    }
    var y = this.incidentXLabelY(chart);
    return 'translate(' + lab.x + ' ' + y + ') rotate(-42)';
  }

  incidentLabelTextX(chart: IncidentTrendChartView, lab: { x: number; text: string }): number {
    if (chart && chart.rotateXLabels) {
      return 0;
    }
    return lab && lab.x ? lab.x : 0;
  }

  incidentLabelTextY(chart: IncidentTrendChartView): number {
    if (chart && chart.rotateXLabels) {
      return 0;
    }
    return this.incidentXLabelY(chart);
  }

  incidentXLabelY(chart: IncidentTrendChartView): number {
    var height = chart && chart.viewHeight ? chart.viewHeight : this.trendChartHeight;
    if (!chart || !chart.rotateXLabels) {
      return height - 16;
    }
    return height - chart.plotBottom + 14;
  }

  incidentXLabelAnchor(chart: IncidentTrendChartView): string {
    if (chart && chart.rotateXLabels) {
      return 'end';
    }
    return 'middle';
  }

  incidentLabelTspanX(chart: IncidentTrendChartView, lab: { x: number; text: string }): number {
    if (chart && chart.rotateXLabels) {
      return 0;
    }
    return lab && lab.x ? lab.x : 0;
  }

  incidentLabelDy(lineIndex: number): string {
    if (lineIndex === 0) {
      return '0';
    }
    return '10';
  }

  incidentLabelLines(lab: { text: string; lines: string[] }): string[] {
    if (lab && lab.lines && lab.lines.length) {
      return lab.lines;
    }
    if (lab && lab.text) {
      return [lab.text];
    }
    return [''];
  }

  private wrapIncidentAxisLabel(text: string): string[] {
    var raw = text ? String(text).replace(/^\s+|\s+$/g, '') : '';
    if (!raw) {
      return [''];
    }
    var chunks: string[] = [];
    if (raw.indexOf('/') >= 0) {
      var slashParts = raw.split('/');
      for (var s = 0; s < slashParts.length; s++) {
        var part = slashParts[s].replace(/^\s+|\s+$/g, '');
        if (part) {
          chunks.push(part);
        }
      }
    } else {
      chunks.push(raw);
    }
    var lines: string[] = [];
    for (var c = 0; c < chunks.length; c++) {
      var wrapped = this.wrapIncidentAxisLabelByWords(chunks[c], 16);
      for (var w = 0; w < wrapped.length; w++) {
        lines.push(wrapped[w]);
      }
    }
    if (!lines.length) {
      lines.push(raw);
    }
    return lines;
  }

  private wrapIncidentAxisLabelByWords(text: string, maxChars: number): string[] {
    var raw = text ? String(text) : '';
    if (!raw) {
      return [];
    }
    if (raw.length <= maxChars) {
      return [raw];
    }
    var words = raw.split(/\s+/);
    var lines: string[] = [];
    var current = '';
    for (var i = 0; i < words.length; i++) {
      var word = words[i];
      if (word.length > maxChars) {
        if (current) {
          lines.push(current);
          current = '';
        }
        var start = 0;
        while (start < word.length) {
          lines.push(word.substring(start, start + maxChars));
          start += maxChars;
        }
        continue;
      }
      var next = current ? current + ' ' + word : word;
      if (next.length > maxChars && current) {
        lines.push(current);
        current = word;
      } else {
        current = next;
      }
    }
    if (current) {
      lines.push(current);
    }
    return lines;
  }

  private incidentRotatedLabelMetrics(xLabels: string[]): { plotBottom: number; plotLeft: number } {
    var maxLineLen = 8;
    var maxLines = 1;
    var list = xLabels || [];
    for (var i = 0; i < list.length; i++) {
      var lines = this.wrapIncidentAxisLabel(list[i]);
      if (lines.length > maxLines) {
        maxLines = lines.length;
      }
      for (var L = 0; L < lines.length; L++) {
        if (lines[L].length > maxLineLen) {
          maxLineLen = lines[L].length;
        }
      }
    }
    var textW = maxLineLen * 4.8;
    var plotBottom = Math.ceil(textW * 0.70 + (maxLines - 1) * 8 + 32);
    if (plotBottom < 120) {
      plotBottom = 120;
    }
    if (plotBottom > 240) {
      plotBottom = 240;
    }
    var plotLeft = Math.ceil(textW * 0.74 + (maxLines - 1) * 6 + 18);
    if (plotLeft < this.trendChartPlot.left) {
      plotLeft = this.trendChartPlot.left;
    }
    if (plotLeft > 160) {
      plotLeft = 160;
    }
    return { plotBottom: plotBottom, plotLeft: plotLeft };
  }

  private buildIncidentCountChart(
    title: string,
    xLabels: string[],
    series: { title: string; values: number[] }[],
    rotateXLabels: boolean,
    editorKey: string,
    tableRows: IncidentTrendEditorRow[]
  ): IncidentTrendChartView {
    var metrics = rotateXLabels
      ? this.incidentRotatedLabelMetrics(xLabels)
      : { plotBottom: this.trendChartPlot.bottom, plotLeft: this.trendChartPlot.left };
    var plotLeft = metrics.plotLeft;
    var plotTop = this.trendChartPlot.top;
    var plotRight = this.trendChartPlot.right;
    var plotBottom = metrics.plotBottom;
    var viewWidth = this.trendChartWidth;
    if (rotateXLabels && xLabels && xLabels.length > 4) {
      viewWidth = Math.max(this.trendChartWidth, xLabels.length * 118);
    }
    var plotH = this.trendChartHeight - plotTop - this.trendChartPlot.bottom;
    var viewHeight = plotTop + plotH + plotBottom;
    var plotW = viewWidth - plotLeft - plotRight;
    var n = xLabels && xLabels.length ? xLabels.length : 0;
    var seriesCount = series && series.length ? series.length : 0;
    var groupW = n > 0 ? plotW / n : plotW;
    var barW = seriesCount > 0
      ? Math.max(6, Math.min(22, groupW / (seriesCount + 1.5)))
      : 10;
    var gap = 2;

    var maxY = 4;
    for (var s = 0; s < seriesCount; s++) {
      for (var v = 0; v < series[s].values.length; v++) {
        if (series[s].values[v] > maxY) {
          maxY = series[s].values[v];
        }
      }
    }
    if (maxY <= 4) {
      maxY = 4;
    } else if (maxY <= 10) {
      maxY = 10;
    } else if (maxY <= 20) {
      maxY = 20;
    } else {
      maxY = Math.ceil(maxY / 5) * 5;
    }

    var step = 1;
    if (maxY > 10) {
      step = 2;
    }
    if (maxY > 20) {
      step = 5;
    }
    if (maxY > 50) {
      step = 10;
    }

    var yLabels: { y: number; text: string }[] = [];
    for (var tick = 0; tick <= maxY; tick += step) {
      var yTick = plotTop + plotH - (tick / maxY) * plotH;
      yLabels.push({ y: yTick, text: String(tick) });
    }

    var bars: { x: number; y: number; w: number; h: number; color: string; tooltip: string }[] = [];
    var labels: { x: number; text: string; lines: string[] }[] = [];
    var clusterW = seriesCount * barW + Math.max(0, seriesCount - 1) * gap;

    for (var p = 0; p < n; p++) {
      var groupX = plotLeft + p * groupW;
      var center = groupX + groupW / 2;
      labels.push({
        x: center,
        text: xLabels[p],
        lines: this.wrapIncidentAxisLabel(xLabels[p])
      });
      var startX = center - clusterW / 2;
      for (var si = 0; si < seriesCount; si++) {
        var count = series[si].values[p] || 0;
        var color = this.incidentChartColors[si % this.incidentChartColors.length];
        var h = (count / maxY) * plotH;
        bars.push({
          x: startX + si * (barW + gap),
          y: plotTop + plotH - h,
          w: barW,
          h: Math.max(h, 0),
          color: color,
          tooltip: xLabels[p] + ' · ' + series[si].title + ': ' + count
        });
      }
    }

    var legendItems: { color: string; label: string }[] = [];
    for (var li = 0; li < seriesCount; li++) {
      legendItems.push({
        color: this.incidentChartColors[li % this.incidentChartColors.length],
        label: series[li].title
      });
    }

    var emptyMessage = '';
    if ((seriesCount === 0 || n === 0) && (!tableRows || tableRows.length === 0)) {
      emptyMessage = 'No records for this home and period.';
    }

    return {
      title: title,
      bars: bars,
      labels: labels,
      yLabels: yLabels,
      legendItems: legendItems,
      emptyMessage: emptyMessage,
      viewWidth: viewWidth,
      viewHeight: viewHeight,
      plotLeft: plotLeft,
      plotBottom: plotBottom,
      rotateXLabels: rotateXLabels,
      editorKey: editorKey,
      tableOpen: this.isIncidentTableOpen(editorKey),
      tableColumnLabels: this.incidentTrendQuarters.slice(),
      tableRows: tableRows || []
    };
  }

  private getFacilityTotalPctByGroup(groupId: number): { label: string; pct: number }[] {
    var points: { label: string; pct: number }[] = [];
    if (this.drugGroupTrendQuarters.length) {
      for (var q = 0; q < this.drugGroupTrendQuarters.length; q++) {
        var label = this.drugGroupTrendQuarters[q];
        var pct = 0;
        for (var i = 0; i < this.drugGroupFacilityTrends.length; i++) {
          var trend = this.drugGroupFacilityTrends[i];
          if (trend.groupId === groupId && trend.label === label) {
            pct = trend.pct || 0;
            break;
          }
        }
        points.push({
          label: label,
          pct: pct
        });
      }
      return points;
    }

    var row: MedicationClassReportRow = null as any;
    for (var r = 0; r < this.allReportRows.length; r++) {
      if (this.allReportRows[r].groupId === groupId) {
        row = this.allReportRows[r];
        break;
      }
    }
    if (!row || !row.cells) {
      return points;
    }
    for (var c = 0; c < row.cells.length; c++) {
      points.push({
        label: row.cells[c].quarterLabel,
        pct: row.cells[c].totalPct || 0
      });
    }
    return points;
  }

  private getAcpPctSeriesForGroup(groupId: number, labels: string[]): number[] {
    var result: number[] = [];
    var hasTrend = false;
    for (var t = 0; t < this.acpCategoryTrends.length; t++) {
      if (this.acpCategoryTrends[t].groupId === groupId) {
        hasTrend = true;
        break;
      }
    }

    if (hasTrend) {
      for (var i = 0; i < labels.length; i++) {
        var found = -1;
        for (var j = 0; j < this.acpCategoryTrends.length; j++) {
          if (this.acpCategoryTrends[j].groupId === groupId
            && this.acpCategoryTrends[j].label === labels[i]) {
            found = this.acpCategoryTrends[j].pct;
            break;
          }
        }
        result.push(found);
      }
      return result;
    }

    var flat = -1;
    for (var a = 0; a < this.allAcpCategoryRows.length; a++) {
      if (this.allAcpCategoryRows[a].groupId === groupId) {
        flat = this.allAcpCategoryRows[a].pct;
        break;
      }
    }
    for (var L = 0; L < labels.length; L++) {
      result.push(flat);
    }
    return result;
  }

  private buildTrendCharts(): void {
    this.trendCharts = [];
    var selectedGroupIds = this.getSelectedGroupIds();
    var homeName = this.selectedHomeName ? this.selectedHomeName : 'Facility';

    for (var g = 0; g < selectedGroupIds.length; g++) {
      var groupId = selectedGroupIds[g];
      var facilityPoints = this.getFacilityTotalPctByGroup(groupId);
      if (!facilityPoints.length) {
        continue;
      }
      var categoryLabel = this.getCategoryLabelForGroupId(groupId);
      this.trendCharts.push(
        this.buildSingleTrendChart(groupId, categoryLabel, homeName, facilityPoints)
      );
    }

    this.trendChartVisible = this.trendCharts.length > 0;
  }

  private buildSingleTrendChart(
    groupId: number,
    categoryLabel: string,
    homeName: string,
    facilityPoints: { label: string; pct: number }[]
  ): TrendChartView {
    var plotLeft = this.trendChartPlot.left;
    var plotTop = this.trendChartPlot.top;
    var plotRight = this.trendChartPlot.right;
    var plotBottom = this.trendChartPlot.bottom;
    var plotW = this.trendChartWidth - plotLeft - plotRight;
    var plotH = this.trendChartHeight - plotTop - plotBottom;
    var n = facilityPoints.length;
    var groupW = plotW / n;
    var barW = Math.max(8, Math.min(26, groupW * 0.26));
    var gap = 4;

    var labelsText: string[] = [];
    for (var i = 0; i < facilityPoints.length; i++) {
      labelsText.push(facilityPoints[i].label);
    }
    var acpSeries = this.getAcpPctSeriesForGroup(groupId, labelsText);

    var maxY = 50;
    for (var m = 0; m < facilityPoints.length; m++) {
      if (facilityPoints[m].pct > maxY) {
        maxY = Math.ceil(facilityPoints[m].pct / 10) * 10;
      }
      if (acpSeries[m] > maxY) {
        maxY = Math.ceil(acpSeries[m] / 10) * 10;
      }
    }
    if (maxY < 10) {
      maxY = 10;
    }

    var yLabels: { y: number; text: string }[] = [];
    for (var tick = 0; tick <= maxY; tick += 10) {
      var yTick = plotTop + plotH - (tick / maxY) * plotH;
      yLabels.push({ y: yTick, text: tick.toFixed(2) + '%' });
    }

    var bars: { x: number; y: number; w: number; h: number; color: string; tooltip: string }[] = [];
    var labels: { x: number; text: string }[] = [];
    var homeLabel = this.selectedHomeName || 'Facility';

    for (var p = 0; p < n; p++) {
      var groupX = plotLeft + p * groupW;
      var center = groupX + groupW / 2;
      var quarterLabel = facilityPoints[p].label;
      labels.push({ x: center, text: quarterLabel });

      var acpPct = acpSeries[p];
      var acpX = center - barW - gap / 2;
      if (acpPct >= 0) {
        var acpH = (acpPct / maxY) * plotH;
        var acpY = plotTop + plotH - acpH;
        bars.push({
          x: acpX,
          y: acpY,
          w: barW,
          h: Math.max(acpH, 0),
          color: '#5dade2',
          tooltip: quarterLabel + ' · Avg ACP: ' + this.formatPct(acpPct) + '%'
        });
      }

      var facPct = facilityPoints[p].pct;
      var facH = (facPct / maxY) * plotH;
      var facY = plotTop + plotH - facH;
      var facX = center + gap / 2;
      bars.push({
        x: facX,
        y: facY,
        w: barW,
        h: Math.max(facH, 0),
        color: '#c0392b',
        tooltip: quarterLabel + ' · ' + homeLabel + ': ' + this.formatPct(facPct) + '%'
      });
    }

    var trendPoints = '';
    if (n >= 2) {
      var sumX = 0;
      var sumY = 0;
      var sumXY = 0;
      var sumXX = 0;
      for (var t = 0; t < n; t++) {
        var xv = t + 1;
        var yv = facilityPoints[t].pct;
        sumX += xv;
        sumY += yv;
        sumXY += xv * yv;
        sumXX += xv * xv;
      }
      var denom = n * sumXX - sumX * sumX;
      var slope = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
      var intercept = (sumY - slope * sumX) / n;
      var linePts: string[] = [];
      for (var L = 0; L < n; L++) {
        var pred = intercept + slope * (L + 1);
        if (pred < 0) {
          pred = 0;
        }
        if (pred > maxY) {
          pred = maxY;
        }
        var lx = plotLeft + L * groupW + groupW / 2 + gap / 2 + barW / 2;
        var ly = plotTop + plotH - (pred / maxY) * plotH;
        linePts.push(lx + ',' + ly);
      }
      trendPoints = linePts.join(' ');
    }

    return {
      title: homeName
        + ' - % of Residents Receiving '
        + categoryLabel
        + ' Q'
        + this.reportQuarter
        + ' '
        + this.reportYear,
      groupId: groupId,
      categoryLabel: categoryLabel,
      bars: bars,
      labels: labels,
      yLabels: yLabels,
      trendPoints: trendPoints
    };
  }

  private buildReportDateLabel(): string {
    var now = new Date();
    var months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return months[now.getMonth()] + ' ' + now.getDate() + ' ' + now.getFullYear();
  }

  private buildDurFacilityNotes(): void {
    var notes: string[] = [];
    var home = this.selectedHomeName || 'Facility';
    var totalRow: AverageRxReportRow = null as any;
    for (var i = 0; i < this.averageRxRows.length; i++) {
      if (this.averageRxRows[i].isTotal) {
        totalRow = this.averageRxRows[i];
        break;
      }
    }
    if (totalRow && totalRow.cells) {
      for (var c = 0; c < totalRow.cells.length; c++) {
        var cell = totalRow.cells[c];
        notes.push(
          'DUR ' + home + ' ' + cell.quarterLabel + ' AVG = '
          + this.formatRate(cell.totalRate)
          + ' M/Resident [Including Treatments]'
        );
      }
    }
    if (notes.length === 0) {
      notes.push('DUR ' + home + ' Q' + this.reportQuarter + ' ' + this.reportYear + ' = ');
    }
    this.durFacilityNotes = notes;
  }

  private loadAvailableReportPeriods(): void {
    this.periodsLoading = true;
    this.periodsLoadError = '';
    var self = this;
    this.statisticsService.getStatisticsRxReportMonths().subscribe(
      function (months: any[]) {
        self.periodsLoading = false;
        self.availableReportMonths = self.parseReportMonths(months);
        self.availableReportQuarters = self.quartersFromMonths(self.availableReportMonths);
        if (!self.availableReportQuarters.length) {
          self.periodYearOptions = [];
          self.periodQuarterOptions = [];
          self.periodsLoadError = 'No StatisticsRx months are loaded yet.';
          return;
        }
        self.selectLatestAvailablePeriod();
      },
      function () {
        self.periodsLoading = false;
        self.availableReportMonths = [];
        self.periodYearOptions = [];
        self.periodQuarterOptions = [];
        self.periodsLoadError = 'Unable to load available report periods.';
      }
    );
  }

  private parseReportMonths(months: any[]): Date[] {
    var list: Date[] = [];
    if (!months || !months.length) {
      return list;
    }
    for (var i = 0; i < months.length; i++) {
      var d = this.parseReportMonth(months[i]);
      if (!d) {
        continue;
      }
      list.push(d);
    }
    list.sort(function (a, b) {
      return a.getTime() - b.getTime();
    });
    return list;
  }

  private quartersFromMonths(months: Date[]): { year: number; quarter: number }[] {
    var seen: { [key: string]: boolean } = {};
    var list: { year: number; quarter: number }[] = [];
    for (var i = 0; i < months.length; i++) {
      var d = months[i];
      var year = d.getFullYear();
      var quarter = Math.floor(d.getMonth() / 3) + 1;
      var key = year + '-' + quarter;
      if (seen[key]) {
        continue;
      }
      seen[key] = true;
      list.push({ year: year, quarter: quarter });
    }
    return list;
  }

  private parseReportMonth(value: any): Date {
    if (!value) {
      return null;
    }
    var text = String(value);
    var parts = text.substring(0, 10).split('-');
    if (parts.length < 3) {
      return null;
    }
    var year = parseInt(parts[0], 10);
    var month = parseInt(parts[1], 10);
    if (!year || !month) {
      return null;
    }
    return new Date(year, month - 1, 1);
  }

  private selectLatestAvailablePeriod(): void {
    var years: number[] = [];
    for (var i = 0; i < this.availableReportQuarters.length; i++) {
      var y = this.availableReportQuarters[i].year;
      if (years.indexOf(y) < 0) {
        years.push(y);
      }
    }
    this.periodYearOptions = years;
    var latest = this.availableReportQuarters[this.availableReportQuarters.length - 1];
    this.periodYear = latest.year;
    this.refreshQuarterOptionsForYear();
    this.periodQuarter = latest.quarter;
    this.syncAnchorFromSelectedPeriod();
  }

  private refreshQuarterOptionsForYear(): void {
    var quarters: number[] = [];
    for (var i = 0; i < this.availableReportQuarters.length; i++) {
      var item = this.availableReportQuarters[i];
      if (item.year === this.periodYear && quarters.indexOf(item.quarter) < 0) {
        quarters.push(item.quarter);
      }
    }
    quarters.sort(function (a, b) { return a - b; });
    this.periodQuarterOptions = quarters;
    if (quarters.length && quarters.indexOf(this.periodQuarter) < 0) {
      this.periodQuarter = quarters[quarters.length - 1];
    }
  }

  private isAvailableReportPeriod(): boolean {
    for (var i = 0; i < this.availableReportQuarters.length; i++) {
      var item = this.availableReportQuarters[i];
      if (item.year === this.periodYear && item.quarter === this.periodQuarter) {
        return true;
      }
    }
    return false;
  }

  /**
   * Selected Report Period = a quarter that exists in StatisticsRx.
   * Tables use this quarter and the two before it. Drug group charts use eight quarters.
   */
  private syncAnchorFromSelectedPeriod(): void {
    this.reportYear = this.periodYear;
    this.reportQuarter = this.periodQuarter;
    this.periodDateRangeLabel = this.periodDateRangeLabelText();
    this.reportPeriodLabel = this.buildPeriodLabel();
  }

  /** Full calendar quarter, even when StatisticsRx has only some months. */
  periodDateRangeLabelText(): string {
    var year = Number(this.periodYear);
    var quarter = Number(this.periodQuarter);
    if (!year || quarter < 1 || quarter > 4) {
      return '';
    }
    var y = String(year);
    if (quarter === 1) {
      return '1 Jan ' + y + ' - 31 Mar ' + y;
    }
    if (quarter === 2) {
      return '1 Apr ' + y + ' - 30 Jun ' + y;
    }
    if (quarter === 3) {
      return '1 Jul ' + y + ' - 30 Sep ' + y;
    }
    return '1 Oct ' + y + ' - 31 Dec ' + y;
  }

  private buildPeriodLabel(): string {
    var labels = this.buildTrailingQuarterLabels(this.reportYear, this.reportQuarter);
    return labels.join(' · ');
  }

  private buildTrailingQuarterLabels(year: number, quarter: number): string[] {
    var labels: string[] = [];
    var y = year;
    var q = quarter;
    for (var i = 0; i < 3; i++) {
      labels.unshift('Q' + q + ' ' + y);
      q = q - 1;
      if (q < 1) {
        q = 4;
        y = y - 1;
      }
    }
    return labels;
  }

  private loadHomeGroups(): void {
    this.homesLoading = true;
    this.homesLoadError = '';
    var self = this;
    this.statisticsService.getDrugStatisticsHomes().subscribe(
      function (groups: any[]) {
        self.homesLoading = false;
        self.homeGroups = self.mapHomeGroups(groups);
        self.syncOrganizationOptions();
        self.refreshVisibleHomeGroups();
        if (!self.homeGroups.length) {
          self.homesLoadError = 'No homes found for Advantage Care Pharmacy or Adelaide Pharmacy.';
        }
      },
      function () {
        self.homesLoading = false;
        self.homeGroups = [];
        self.visibleHomeGroups = [];
        self.homesLoadError = 'Unable to load homes. Please try again.';
      }
    );
  }

  private mapHomeGroups(data: any[]): DrugStatisticsHomeGroup[] {
    var groups: DrugStatisticsHomeGroup[] = [];
    if (!data || !data.length) {
      return groups;
    }
    for (var i = 0; i < data.length; i++) {
      var row = data[i];
      var pharmacyIdRaw = this.pickDtoField(row, 'pharmacyId', 'PharmacyId');
      if (pharmacyIdRaw == null || pharmacyIdRaw === '') {
        continue;
      }
      var pharmacyId = parseInt(String(pharmacyIdRaw), 10);
      if (!pharmacyId || pharmacyId <= 0 || isNaN(pharmacyId)) {
        continue;
      }
      var pharmacyName = this.pickDtoField(row, 'pharmacyName', 'PharmacyName');
      var homes = this.pickDtoField(row, 'homes', 'Homes');
      groups.push({
        pharmacyId: pharmacyId,
        pharmacyName: pharmacyName || ('Pharmacy ' + pharmacyId),
        homes: this.mapFacilityOptions(homes || [])
      });
    }
    return groups;
  }

  private mapFacilityOptions(data: any[]): DrugStatisticsHomeOption[] {
    var options: DrugStatisticsHomeOption[] = [];
    if (!data || !data.length) {
      return options;
    }
    for (var i = 0; i < data.length; i++) {
      var row = data[i];
      var idRaw = this.pickDtoField(row, 'id', 'Id');
      if (idRaw == null || idRaw === '') {
        continue;
      }
      var id = parseInt(String(idRaw), 10);
      if (!id || id <= 0 || isNaN(id)) {
        continue;
      }
      var name = this.pickDtoField(row, 'name', 'Name');
      options.push({
        id: id,
        name: name || ('Facility ' + id)
      });
    }
    return options.slice().sort(function (a, b) {
      var left = (a.name || '').toLowerCase();
      var right = (b.name || '').toLowerCase();
      if (left < right) {
        return -1;
      }
      if (left > right) {
        return 1;
      }
      return 0;
    });
  }

  private syncOrganizationOptions(): void {
    var options: { id: number; name: string }[] = [{ id: 0, name: 'All' }];
    var seen: { [id: number]: boolean } = {};
    for (var i = 0; i < this.homeGroups.length; i++) {
      var group = this.homeGroups[i];
      if (!group || seen[group.pharmacyId]) {
        continue;
      }
      seen[group.pharmacyId] = true;
      options.push({
        id: group.pharmacyId,
        name: group.pharmacyName || ('Pharmacy ' + group.pharmacyId)
      });
    }
    var defaults: { id: number; name: string }[] = [
      { id: 3, name: 'Advantage Care Pharmacy' },
      { id: 26, name: 'Adelaide Pharmacy' }
    ];
    for (var d = 0; d < defaults.length; d++) {
      if (!seen[defaults[d].id]) {
        options.push(defaults[d]);
      }
    }
    this.organizationOptions = options;
  }

  private pickDtoField(row: any, camel: string, pascal: string): any {
    if (!row) {
      return null;
    }
    if (Object.prototype.hasOwnProperty.call(row, camel) && row[camel] != null) {
      return row[camel];
    }
    if (Object.prototype.hasOwnProperty.call(row, pascal)) {
      return row[pascal];
    }
    return row[camel];
  }

  private parseSelectedHomeKey(): void {
    this.selectedPharmacyId = null;
    this.selectedHomeId = null;
    if (!this.selectedHomeKey) {
      return;
    }
    var parts = String(this.selectedHomeKey).split(':');
    if (parts.length !== 2) {
      return;
    }
    var pharmacyId = parseInt(parts[0], 10);
    var facilityId = parseInt(parts[1], 10);
    if (isNaN(pharmacyId) || isNaN(facilityId) || pharmacyId <= 0 || facilityId <= 0) {
      return;
    }
    this.selectedPharmacyId = pharmacyId;
    this.selectedHomeId = facilityId;
  }

  private resetUnits(): void {
    this.selectedUnitIds = [];
    this.unitOptions = [];
    this.unitsLoading = false;
    this.unitsLoadError = '';
    this.unitsPanelOpen = false;
  }

  private loadUnitsForHome(pharmacyId: number, facilityId: number): void {
    this.unitsLoading = true;
    this.unitsLoadError = '';
    var self = this;
    this.sharedService.getFacilityUnitsForFacility(facilityId, pharmacyId).subscribe(
      function (data) {
        self.unitsLoading = false;
        self.unitOptions = self.mapUnitOptions(data);
        self.selectedUnitIds = [];
        for (var i = 0; i < self.unitOptions.length; i++) {
          self.selectedUnitIds.push(self.unitOptions[i].id);
        }
      },
      function () {
        self.unitsLoading = false;
        self.unitOptions = [];
        self.selectedUnitIds = [];
        self.unitsLoadError = 'Unable to load home units. Please try again.';
      }
    );
  }

  private mapUnitOptions(data: any[]): { id: number; name: string }[] {
    var options: { id: number; name: string }[] = [];
    if (!data || !data.length) {
      return options;
    }
    for (var i = 0; i < data.length; i++) {
      var row = data[i];
      if (!row || row.id == null) {
        continue;
      }
      options.push({
        id: row.id,
        name: row.name || ('Unit ' + row.id)
      });
    }
    return options.slice().sort(function (a, b) {
      var left = (a.name || '').toLowerCase();
      var right = (b.name || '').toLowerCase();
      if (left < right) {
        return -1;
      }
      if (left > right) {
        return 1;
      }
      return 0;
    });
  }

  private updateUnitsPanelPosition(): void {
    var triggerEl = this.unitsTrigger && this.unitsTrigger.nativeElement;
    if (!triggerEl) {
      return;
    }
    var rect = triggerEl.getBoundingClientRect();
    var maxH = Math.max(120, window.innerHeight - rect.bottom - 12);
    this.unitsPanelStyle = {
      position: 'fixed',
      top: (rect.bottom + 2) + 'px',
      left: rect.left + 'px',
      width: rect.width + 'px',
      'max-height': maxH + 'px',
      'z-index': '1060'
    };
  }

  private updateHomesPanelPosition(): void {
    var triggerEl = this.homesTrigger && this.homesTrigger.nativeElement;
    if (!triggerEl) {
      return;
    }
    var rect = triggerEl.getBoundingClientRect();
    var maxH = Math.max(160, window.innerHeight - rect.bottom - 12);
    this.homesPanelStyle = {
      position: 'fixed',
      top: (rect.bottom + 2) + 'px',
      left: rect.left + 'px',
      width: Math.max(rect.width, 260) + 'px',
      'max-height': maxH + 'px',
      'z-index': '2100'
    };
  }
}
